interface ErrorIdentity {
    /** The error instance the HTTP layer captures. */
    error: unknown
    /** Response shared with the error the caller catches, when one exists. */
    response?: unknown
    /** Underlying error shared with the error the caller catches, when one exists. */
    cause?: unknown
}

interface DeferredCapture {
    identity: ErrorIdentity
    /** The scopes that were open when the error arrived — any of them might consume it. */
    scopes: Set<symbol>
    capture: () => void
}

// Intentionally module-scoped and client-only: within a single browser context this is the
// only channel between the HTTP layers and useMutation. Every entry point falls through on
// the server, so no state is shared across SSR requests.
const activeScopes = new Set<symbol>()
let deferred: DeferredCapture[] = []

export function isMutationErrorCaptureActive(): boolean {
    return activeScopes.size > 0
}

/**
 * Captures an HTTP-layer error immediately, unless a mutation capture scope is active.
 * Inside a scope the capture is deferred so the mutation catch can consume the error it
 * reports itself (with labels). A deferred capture is held only while the scopes that were
 * open when it arrived are still running; once the last of them settles it is flushed
 * unchanged. Best-effort: navigating away during that window can lose the capture.
 */
export function captureHttpError(identity: ErrorIdentity, capture: () => void): void {
    if (import.meta.server || activeScopes.size === 0) {
        capture()
        return
    }

    deferred.push({ identity, scopes: new Set(activeScopes), capture })
}

/** Drops deferred captures for the error a mutation catch reports itself. */
export function consumeMutationErrorCapture(error: unknown): void {
    if (import.meta.server) return

    deferred = deferred.filter(entry => !matches(entry.identity, error))
}

function matches(identity: ErrorIdentity, error: unknown): boolean {
    if (identity.error === error) return true
    if (typeof error !== 'object' || error === null) return false

    const candidate = error as { response?: unknown, cause?: unknown }
    if (identity.response !== undefined && identity.response === candidate.response) return true
    if (identity.cause !== undefined && (identity.cause === error || identity.cause === candidate.cause)) return true

    return false
}

/**
 * Opens a capture scope around `fn`. When `fn` rejects, deferred captures matching its error
 * are consumed before the error rethrows. Callers must keep navigation (and anything else
 * that can unload the page) outside `fn`, so deferred captures flush before it happens.
 */
export async function runWithMutationErrorCapture<T>(fn: () => Promise<T>): Promise<T> {
    if (import.meta.server) return fn()

    const scope = Symbol('mutation-error-capture-scope')
    activeScopes.add(scope)

    try {
        return await fn()
    }
    catch (error) {
        consumeMutationErrorCapture(error)
        throw error
    }
    finally {
        activeScopes.delete(scope)
        settleScope(scope)
    }
}

/** Flushes deferred captures that no longer have an open scope that could consume them. */
function settleScope(scope: symbol): void {
    const remaining: DeferredCapture[] = []
    const settled: DeferredCapture[] = []

    for (const entry of deferred) {
        entry.scopes.delete(scope)
        if (entry.scopes.size) remaining.push(entry)
        else settled.push(entry)
    }

    deferred = remaining

    for (const entry of settled) {
        try {
            entry.capture()
        }
        catch {
            // A throwing capture must never mask the mutation's own error.
        }
    }
}
